import React, { useRef, useState } from 'react';
import { Box, Button, TextField, Typography, Paper, IconButton, InputAdornment, Checkbox, FormControlLabel, Link } from '@mui/material';
import api from '../api';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ChatIcon from '@mui/icons-material/Chat';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';


function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(true);
  const navigate = useNavigate();

  const inputRefs = useRef([]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const payload = { username, password };
      const res = await api.post('/auth/login', payload);
      const token = res.data.token;
      const userName = res?.data?.user?.name;
      const userRole = res?.data?.user?.role?.TA_Role_Name ? res?.data?.user?.role?.TA_Role_Name : 'Admin';
      const userRoleId = res?.data?.user?.role?._id;
      const userid=res?.data?.user?.id;      

      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('name', userName);
        localStorage.setItem('userId', userid);
        localStorage.setItem('userRole', userRole);
        localStorage.setItem('userRoleId', userRoleId);
      }
      onLogin();
      window.location.reload();
    } catch (err) {
      alert('Login failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // To move to next textfield by pressing enter

    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const nextEl = inputRefs.current[index + 1];
          if (nextEl) {
            nextEl.focus();
          } else {
            handleLogin();
            
          }
        }
      };

  const pageGradient = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        '::before': {
          content: '""',
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          background: 'linear-gradient(120deg, #e0f7fa, #b2ebf2, 	#ff6fa1, #29b6f6, #0088cc)',
          backgroundSize: '300% 300%',
          animation: `${pageGradient} 12s ease-in-out infinite`,
          opacity: 0.7,
        },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // stack on small screens
          width: { xs: '90%', sm: '85%', md: '90%', lg: 1100 },
          minHeight: { xs: 'auto', md: 600 },
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: 6,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Left Panel */}
        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Box sx={{textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center',mr: { xs: 5,md:0 } }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 400 }}>Welcome to</Typography>
            <ChatIcon sx={{ fontSize: 64, mb: 1, color: '#fff' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, letterSpacing: 1 }}>SappSapp</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, maxWidth: 260 }}>
              We provide a communication hub between public users and your team.
            </Typography>
          </Box>
        </Box>
        {/* Right Panel (Form) */}
        <Box
          sx={{
            flex: 1,
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 3, sm: 4, md: 5 },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              fontWeight: 600,
              fontSize: { xs: '1.4rem', sm: '1.7rem' },
            }}
          >
            Login to your account
          </Typography>

          <Box sx={{ width: '100%', maxWidth: 320 }}>
            <TextField
              label="Username"
              fullWidth
              margin="normal"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              sx={{ mb: 2 }}
              inputRef={el => (inputRefs.current[0] = el)}
              onKeyDown={e => handleKeyDown(e, 0)}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              inputRef={el => (inputRefs.current[1] = el)}
              onKeyDown={e => handleKeyDown(e, 1)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(show => !show)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{
                borderRadius: 3,
                fontWeight: 700,
                textTransform: 'none',
                bgcolor: '#0088cc',
                py: 1.5,
                '&:hover': {
                  bgcolor: '#1976d2',
                },
              }}
              onClick={handleLogin}
              disabled={loading || !checked}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              sx={{
                borderRadius: 3,
                fontWeight: 700,
                textTransform: 'none',
                mt: 2,
                py: 1.5,
                borderColor: '#0088cc',
                color: '#0088cc',
                '&:hover': {
                  bgcolor: '#e0f7fa',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                },
              }}
              onClick={() => navigate('/doctor-login')}
            >
              Expert Login
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default LoginPage;
